import { useState, useEffect, type FormEvent } from 'react';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, resetCustomerTelegramIdentity } from '../api/customers';
import { getCompanies } from '../api/companies';
import { listSgpOperations, getSgp360View, type Sgp360View } from '../api/sgp';
import { useToast } from '../context/ToastContext';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SgpOperationsPanel } from '../components/SgpOperationsPanel';
import { Topbar } from '../layout/Topbar';
import type { Customer, Company, SgpOperationDefinition } from '../types';

export function CustomersPage() {
  const { addToast } = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Customer | null>(null);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmForgetIdentity, setConfirmForgetIdentity] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state - full customer
  const [formName, setFormName] = useState('');
  const [formDocument, setFormDocument] = useState('');
  const [formCustomerType, setFormCustomerType] = useState('');
  const [formBirthDate, setFormBirthDate] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formMobile, setFormMobile] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formStreet, setFormStreet] = useState('');
  const [formNumber, setFormNumber] = useState('');
  const [formComplement, setFormComplement] = useState('');
  const [formReference, setFormReference] = useState('');
  const [formDistrict, setFormDistrict] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formState, setFormState] = useState('');
  const [formZipCode, setFormZipCode] = useState('');
  const [formCompanyId, setFormCompanyId] = useState('');
  const [formIsLead, setFormIsLead] = useState(false);

  // SGP Plans
  const [sgpPlans, setSgpPlans] = useState<Array<{ id: number; nome: string; valor: number }>>([]);
  const [showPlans, setShowPlans] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);

  // SGP 360° view
  const [sgp360Data, setSgp360Data] = useState<Sgp360View | null>(null);
  const [sgp360Loading, setSgp360Loading] = useState(false);
  const [sgp360Error, setSgp360Error] = useState<string | null>(null);
  const [showSgpOps, setShowSgpOps] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cust, comp] = await Promise.all([getCustomers(), getCompanies()]);
      setCustomers(cust);
      setCompanies(comp);
    } catch {
      addToast('error', 'Falha ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = customers.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name?.toLowerCase().includes(q)) ||
           (c.document?.includes(q)) ||
           (c.phone?.includes(q)) ||
           (c.email?.toLowerCase().includes(q));
  });

  const clearForm = () => {
    setSelected(null);
    setFormName(''); setFormDocument(''); setFormCustomerType(''); setFormBirthDate('');
    setFormPhone(''); setFormMobile(''); setFormEmail('');
    setFormStreet(''); setFormNumber(''); setFormComplement(''); setFormReference('');
    setFormDistrict('');
    setFormCity(''); setFormState(''); setFormZipCode('');
    setFormCompanyId(companies[0]?.id || ''); setFormIsLead(false);
  };

  const selectCustomer = (c: Customer) => {
    setSelected(c);
    setFormName(c.name || ''); setFormDocument(c.document || '');
    setFormCustomerType(c.customerType || ''); setFormBirthDate(c.birthDate ? c.birthDate.split('T')[0] : '');
    setFormPhone(c.phone || ''); setFormMobile(c.mobile || ''); setFormEmail(c.email || '');
    setFormStreet(c.addressStreet || ''); setFormNumber(c.addressNumber || '');
    setFormComplement(c.addressComplement || ''); setFormReference(c.addressReference || '');
    setFormDistrict(c.addressDistrict || '');
    setFormCity(c.addressCity || ''); setFormState(c.addressState || '');
    setFormZipCode(c.addressZipCode || ''); setFormCompanyId(c.companyId); setFormIsLead(c.isLead);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    setSaving(true);
    const data = {
      name: formName, document: formDocument || undefined,
      customerType: formCustomerType || undefined,
      birthDate: formBirthDate ? new Date(`${formBirthDate}T00:00:00`).toISOString() : undefined,
      phone: formPhone || undefined, mobile: formMobile || undefined,
      email: formEmail || undefined,
      addressStreet: formStreet || undefined, addressNumber: formNumber || undefined,
      addressComplement: formComplement || undefined, addressDistrict: formDistrict || undefined,
      addressReference: formReference || undefined,
      addressCity: formCity || undefined, addressState: formState || undefined,
      addressZipCode: formZipCode || undefined,
      isLead: formIsLead,
    };
    try {
      if (selected) {
        const updated = await updateCustomer(selected.id, data);
        setCustomers((prev) => prev.map((c) => c.id === updated.id ? updated : c));
        addToast('success', 'Cliente atualizado');
      } else {
        const created = await createCustomer({ ...data, companyId: formCompanyId });
        setCustomers((prev) => [...prev, created]);
        addToast('success', 'Cliente criado');
      }
      clearForm();
    } catch {
      addToast('error', 'Falha ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCustomer(id);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      if (selected?.id === id) clearForm();
      addToast('success', 'Cliente excluído');
    } catch {
      addToast('error', 'Falha ao excluir cliente');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleForgetIdentity = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await resetCustomerTelegramIdentity(selected.id);
      setCustomers((current) => current.map((customer) => customer.id === updated.id ? updated : customer));
      selectCustomer(updated);
      setSgp360Data(null);
      setSgp360Error(null);
      setConfirmForgetIdentity(false);
      addToast('success', 'Identificação esquecida. O bot solicitará o CPF/CNPJ novamente.');
    } catch (error) {
      addToast('error', error instanceof Error ? error.message : 'Falha ao esquecer a identificação');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadPlans = async () => {
    setPlansLoading(true);
    try {
      const ops = await listSgpOperations();
      const plans = (ops as any[]).filter((o: any) => o.id === 'sgp.plan.list');
      setSgpPlans([]);
      setShowPlans(true);
      addToast('info', 'Consulte o Dashboard SGP para ver planos disponíveis');
    } catch {
      addToast('error', 'Falha ao carregar planos');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleLoadSgp360 = async () => {
    if (!selected?.id) {
      addToast('error', 'Selecione um cliente salvo para consultar o SGP 360°');
      return;
    }
    setSgp360Loading(true);
    setSgp360Error(null);
    try {
      const data = await getSgp360View(selected.id);
      setSgp360Data(data);
    } catch (err: any) {
      setSgp360Error(err.message || 'Falha ao consultar SGP 360°');
      setSgp360Data(null);
    } finally {
      setSgp360Loading(false);
    }
  };

  const handleCloseSgp360 = () => { setSgp360Data(null); setSgp360Error(null); };

  return (
    <div className="view-container">
      <Topbar title="Clientes" subtitle="Cadastro completo com endereço e integração SGP." />

      <div className="records-layout">
        {/* ── List ── */}
        <div className="panel">
          <div className="panel-header">
            <h2>Clientes ({customers.length})</h2>
            <button onClick={clearForm}>+ Novo</button>
          </div>
          <div className="queue-filter">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, documento, telefone ou email..."
              style={{ fontSize: '0.857rem' }}
            />
          </div>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state"><p>{search ? 'Nenhum cliente encontrado para esta busca.' : 'Nenhum cliente cadastrado.'}</p></div>
          ) : (
            <div className="data-list" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {filtered.map((c) => (
                <div key={c.id} className={`data-list-item${selected?.id === c.id ? ' active' : ''}`}
                  onClick={() => selectCustomer(c)}>
                  <div>
                    <div className="item-name">{c.name || 'Sem nome'}</div>
                    <div className="item-detail">
                      {c.document ? `${c.document} — ` : ''}
                      {c.phone || c.mobile || 'sem contato'}
                      {c.addressCity ? ` • ${c.addressCity}/${c.addressState || ''}` : ''}
                    </div>
                  </div>
                  <span className={`pill ${c.isLead ? 'warning' : 'primary'}`}>
                    {c.isLead ? 'Lead' : 'Cliente'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Form ── */}
        <div className="panel">
            <div className="panel-header">
              <h2>{selected ? 'Editar' : 'Novo'} cliente</h2>
              <div className="flex gap-sm">
                <button className="secondary" onClick={handleLoadPlans} disabled={plansLoading} style={{ fontSize: '0.786rem' }}>
                  {plansLoading ? '🔄' : '📋'} Planos SGP
                </button>
                {selected && selected.id && (
                  <>
                    <button className="secondary" onClick={handleLoadSgp360} disabled={sgp360Loading} style={{ fontSize: '0.786rem' }}>
                      {sgp360Loading ? '🔄' : '🔍'} SGP 360°
                    </button>
                    <button onClick={() => setShowSgpOps(true)} style={{ fontSize: '0.786rem' }}>
                      ⚡ Operações SGP
                    </button>
                  </>
                )}
              </div>
            </div>
          <div className="panel-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* SGP 360° data */}
            {sgp360Error && (
              <div style={{ padding: 10, marginBottom: 12, borderRadius: 'var(--radius-sm)', background: 'var(--color-danger-bg, #fdecea)', border: '1px solid var(--color-danger, #dc3545)', fontSize: '0.857rem', color: 'var(--color-danger, #dc3545)' }}>
                {sgp360Error}
                <button className="secondary" onClick={handleCloseSgp360} style={{ marginLeft: 8, padding: '2px 8px', fontSize: '0.786rem' }}>×</button>
              </div>
            )}
            {sgp360Data && (
              <div style={{ marginBottom: 16, padding: 14, borderRadius: 'var(--radius-sm)', background: 'var(--color-primary-bg, #e8f4fd)', border: '1px solid var(--color-primary, #0d6efd)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <strong style={{ fontSize: '0.929rem' }}>SGP 360° — {sgp360Data.customer?.name || 'Cliente'}</strong>
                  <button className="secondary" onClick={handleCloseSgp360} style={{ padding: '2px 8px', fontSize: '0.786rem' }}>Fechar</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.857rem' }}>
                  <div><span style={{ color: 'var(--text-secondary)' }}>CPF/CNPJ:</span> {sgp360Data.customer?.document || '—'}</div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Código:</span> {sgp360Data.customer?.code || '—'}</div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Status:</span> <span className={`pill ${sgp360Data.customer?.status === 'ATIVO' ? 'success' : 'warning'}`}>{sgp360Data.customer?.status || '—'}</span></div>
                  <div><span style={{ color: 'var(--text-secondary)' }}>Cadastro:</span> {sgp360Data.customer?.registrationDate || '—'}</div>
                </div>
                {sgp360Data.contracts && sgp360Data.contracts.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: '0.786rem', color: 'var(--text-secondary)' }}>Contratos ({sgp360Data.contracts.length})</strong>
                    {sgp360Data.contracts.map((ct, i) => (
                      <div key={i} style={{ padding: '6px 0', borderTop: '1px solid var(--border-color)', fontSize: '0.857rem', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{ct.code || ct.id} — {ct.planName || 'plano'}</span>
                        <span className={`pill ${ct.status === 'ATIVO' ? 'success' : 'neutral'}`}>{ct.status || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
                {sgp360Data.services && sgp360Data.services.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: '0.786rem', color: 'var(--text-secondary)' }}>Serviços ({sgp360Data.services.length})</strong>
                    {sgp360Data.services.map((sv, i) => (
                      <div key={i} style={{ padding: '6px 0', borderTop: '1px solid var(--border-color)', fontSize: '0.857rem', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Serviço #{sv.id} — {sv.type || sv.name || '—'}</span>
                        <span className={`pill ${sv.status === 'ATIVO' ? 'success' : 'neutral'}`}>{sv.status || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <form className="form-stack" onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={{ gridColumn: '1 / -1' }}>Nome <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Nome completo" required /></label>
                <label>Documento <input value={formDocument} onChange={(e) => setFormDocument(e.target.value)} placeholder="CPF ou CNPJ" /></label>
                <label>Tipo
                  <select value={formCustomerType} onChange={(e) => setFormCustomerType(e.target.value)}>
                    <option value="">Selecione</option>
                    <option value="PF">PF</option>
                    <option value="PJ">PJ</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label>Telefone <input value={formPhone} onChange={(e) => setFormPhone(e.target.value)} placeholder="(11) 99999-9999" /></label>
                <label>Celular <input value={formMobile} onChange={(e) => setFormMobile(e.target.value)} placeholder="(11) 98888-8888" /></label>
                <label style={{ gridColumn: '1 / -1' }}>E-mail <input value={formEmail} onChange={(e) => setFormEmail(e.target.value)} type="email" placeholder="cliente@email.com" /></label>
                <label>Nascimento <input value={formBirthDate} onChange={(e) => setFormBirthDate(e.target.value)} type="date" /></label>
                <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 22 }}>
                  <input type="checkbox" checked={formIsLead} onChange={(e) => setFormIsLead(e.target.checked)} />
                  Lead (não localizado)
                </label>
              </div>

              <fieldset style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', padding: 12 }}>
                <legend style={{ fontWeight: 600, fontSize: '0.786rem', color: 'var(--text-secondary)' }}>Endereço</legend>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
                  <label>Logradouro <input value={formStreet} onChange={(e) => setFormStreet(e.target.value)} placeholder="Rua" /></label>
                  <label>Número <input value={formNumber} onChange={(e) => setFormNumber(e.target.value)} placeholder="123" /></label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                  <label>Complemento <input value={formComplement} onChange={(e) => setFormComplement(e.target.value)} /></label>
                  <label>Ponto de Referência <input value={formReference} onChange={(e) => setFormReference(e.target.value)} placeholder="Próximo a... (ex.: mercado, escola)" /></label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                  <label>Bairro <input value={formDistrict} onChange={(e) => setFormDistrict(e.target.value)} /></label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8, marginTop: 8 }}>
                  <label>Cidade <input value={formCity} onChange={(e) => setFormCity(e.target.value)} /></label>
                  <label>UF <input value={formState} onChange={(e) => setFormState(e.target.value)} maxLength={2} /></label>
                  <label>CEP <input value={formZipCode} onChange={(e) => setFormZipCode(e.target.value)} placeholder="00000-000" /></label>
                </div>
              </fieldset>

              <label>Empresa
                <select value={formCompanyId} onChange={(e) => setFormCompanyId(e.target.value)}>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>

              <div className="flex gap-sm">
                <button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
                <button className="secondary" onClick={clearForm} type="button">Cancelar</button>
                {selected && <button className="secondary customer-forget-button" onClick={() => setConfirmForgetIdentity(true)} type="button" disabled={saving}>Esquecer identificação</button>}
                {selected && <button className="danger" onClick={() => setConfirmDelete(selected.id)} type="button">Excluir</button>}
              </div>
            </form>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir cliente"
        message="Tem certeza? Todas as informações do cliente e conversas associadas serão removidas."
        confirmLabel="Excluir"
        onConfirm={() => handleDelete(confirmDelete!)}
        onCancel={() => setConfirmDelete(null)}
      />

      <ConfirmDialog
        open={confirmForgetIdentity}
        title="Esquecer identificação do cliente"
        message={<>O CPF/CNPJ, o vínculo local com o SGP e a memória aprendida pela IA serão removidos. O histórico será preservado e nada será alterado no SGP. Na próxima mensagem, o bot solicitará o documento novamente.</>}
        confirmLabel="Esquecer identificação"
        variant="danger"
        onConfirm={handleForgetIdentity}
        onCancel={() => setConfirmForgetIdentity(false)}
      />

      {showSgpOps && selected && (
        <SgpOperationsPanel customer={selected} onClose={() => setShowSgpOps(false)} />
      )}
    </div>
  );
}
